<script setup>
/* eslint-disable no-alert */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import LingxingConfigAPI from 'dashboard/api/lingxingConfig';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';

const STATUS_LABELS = {
  unconfigured: '未配置',
  connected: '已连接',
  verification_failed: '验证失败',
  disabled: '已停用',
};

const isLoading = ref(true);
const isSaving = ref(false);
const isVerifying = ref(false);
const isSyncing = ref(false);
const config = ref(null);
const form = reactive({
  appId: '',
  appSecret: '',
  enabled: false,
  autoSyncEnabled: false,
});

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(date);
};

const errorMessage = (error, fallback) => {
  const data = error?.response?.data || {};
  return [
    data.error || data.message || fallback,
    data.detail && data.detail !== data.error ? `领星返回：${data.detail}` : '',
    data.code ? `错误码：${data.code}` : '',
    data.egress_ip ? `服务器出口 IP：${data.egress_ip}` : '',
    data.suggestion ? `处理建议：${data.suggestion}` : '',
  ]
    .filter(Boolean)
    .join('\n');
};

const statusItems = computed(() => {
  const data = config.value || {};
  return [
    ['状态', STATUS_LABELS[data.status] || data.status || '未配置'],
    ['AppSecret', data.secret_configured ? '已配置' : '未配置'],
    ['最近验证', formatDate(data.last_verified_at)],
    ['最近连接', formatDate(data.last_connected_at)],
    ['凭证更新', formatDate(data.credentials_updated_at)],
    ['最近错误', data.last_error_code || '—'],
    ['API 主机', data.api_host || 'openapi.lingxing.com'],
    ['白名单 IP', data.egress_ip || window.location.hostname],
  ];
});

const canSyncExisting = computed(() => {
  const data = config.value;
  return Boolean(
    data?.can_configure &&
      data.status === 'connected' &&
      data.enabled &&
      data.auto_sync_enabled
  );
});

const loadConfig = async () => {
  try {
    isLoading.value = true;
    const { data } = await LingxingConfigAPI.get();
    config.value = data;
    form.appId = data.app_id || '';
    form.appSecret = '';
    form.enabled = Boolean(data.enabled);
    form.autoSyncEnabled = Boolean(data.auto_sync_enabled);
  } catch (error) {
    useAlert(errorMessage(error, '无法读取领星配置'));
  } finally {
    isLoading.value = false;
  }
};

const saveConfig = async () => {
  try {
    isSaving.value = true;
    await LingxingConfigAPI.update({
      app_id: form.appId.trim(),
      app_secret: form.appSecret || undefined,
      enabled: form.enabled,
      auto_sync_enabled: form.enabled && form.autoSyncEnabled,
    });
    form.appSecret = '';
    useAlert(form.enabled ? '领星配置已保存并验证成功' : '领星配置已保存');
    await loadConfig();
  } catch (error) {
    useAlert(errorMessage(error, '保存领星配置失败'));
  } finally {
    isSaving.value = false;
  }
};

const verifyConfig = async () => {
  try {
    isVerifying.value = true;
    await LingxingConfigAPI.verify();
    useAlert('领星连接验证成功');
    await loadConfig();
  } catch (error) {
    useAlert(errorMessage(error, '领星连接验证失败'));
  } finally {
    isVerifying.value = false;
  }
};

const syncExisting = async () => {
  if (
    !window.confirm(
      '将扫描全站所有账户的现有会话，并按唯一订单号排队同步。是否继续？'
    )
  ) {
    return;
  }

  try {
    isSyncing.value = true;
    const { data } = await LingxingConfigAPI.syncExisting();
    useAlert(data.message || '已开始后台同步');
  } catch (error) {
    useAlert(errorMessage(error, '启动现有会话同步失败'));
  } finally {
    isSyncing.value = false;
  }
};

const handleEnabledChange = () => {
  if (!form.enabled) form.autoSyncEnabled = false;
};

onMounted(loadConfig);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<template>
  <SettingsLayout :is-loading="isLoading" loading-message="正在读取领星配置…">
    <template #header>
      <BaseSettingsHeader
        title="领星 ERP"
        description="配置全站唯一的领星连接；所有 Chatwoot 账户共用该配置。"
      />
    </template>

    <template #body>
      <div
        class="rounded-xl border border-n-amber-6 bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
      >
        此配置对整个网站生效。修改凭证或停用自动同步将影响所有账户。
      </div>

      <SectionLayout
        title="连接状态"
        description="凭证只由服务器使用，AppSecret 保存后不会再显示。"
      >
        <dl
          class="grid gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-5 text-sm sm:grid-cols-2"
        >
          <div
            v-for="[label, value] in statusItems"
            :key="label"
            class="flex gap-2"
          >
            <dt class="text-n-slate-9">{{ label }}：</dt>
            <dd class="text-n-slate-12">{{ value }}</dd>
          </div>
        </dl>
      </SectionLayout>

      <SectionLayout
        with-border
        title="全局凭证"
        description="在领星开放平台获取 AppID/AppSecret，并将上方生产出口 IP 加入白名单。"
      >
        <form
          v-if="config?.can_configure"
          class="grid max-w-2xl gap-5"
          @submit.prevent="saveConfig"
        >
          <label class="grid gap-2 text-sm text-n-slate-12">
            <span>AppID</span>
            <input
              v-model="form.appId"
              type="text"
              required
              autocomplete="off"
              class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <label class="grid gap-2 text-sm text-n-slate-12">
            <span>AppSecret</span>
            <input
              v-model="form.appSecret"
              type="password"
              :required="!config?.secret_configured"
              autocomplete="new-password"
              :placeholder="
                config?.secret_configured
                  ? '留空表示保持当前 AppSecret'
                  : '请输入 AppSecret'
              "
              class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <label class="flex items-center gap-3 text-sm text-n-slate-12">
            <input
              v-model="form.enabled"
              type="checkbox"
              class="h-4 w-4"
              @change="handleEnabledChange"
            />
            启用领星连接
          </label>

          <label class="flex items-center gap-3 text-sm text-n-slate-12">
            <input
              v-model="form.autoSyncEnabled"
              type="checkbox"
              :disabled="!form.enabled"
              class="h-4 w-4"
            />
            会话出现 Amazon 订单号后自动获取并保存订单详情
          </label>

          <div class="flex flex-wrap gap-3">
            <button
              type="submit"
              :disabled="isSaving"
              class="rounded-lg bg-n-brand px-4 py-2 text-sm text-white disabled:opacity-60"
            >
              {{
                isSaving
                  ? form.enabled
                    ? '正在验证…'
                    : '正在保存…'
                  : '保存并验证'
              }}
            </button>
            <button
              type="button"
              :disabled="isVerifying || !config?.secret_configured"
              class="rounded-lg bg-n-alpha-3 px-4 py-2 text-sm text-n-slate-12 disabled:opacity-60"
              @click="verifyConfig"
            >
              {{ isVerifying ? '正在验证…' : '重新验证' }}
            </button>
          </div>
        </form>

        <div
          v-else
          class="rounded-xl border border-n-weak p-4 text-sm text-n-slate-11"
        >
          该配置由网站指定的全局配置账户管理；当前账户仅可查看连接状态。
        </div>
      </SectionLayout>

      <SectionLayout
        with-border
        title="现有会话"
        description="按唯一订单号扫描全站已有会话，并在后台排队同步。"
      >
        <button
          v-if="config?.can_configure"
          type="button"
          :disabled="isSyncing || !canSyncExisting"
          class="rounded-lg bg-n-alpha-3 px-4 py-2 text-sm text-n-slate-12 disabled:opacity-60"
          @click="syncExisting"
        >
          {{ isSyncing ? '正在启动…' : '同步现有会话订单' }}
        </button>
      </SectionLayout>
    </template>
  </SettingsLayout>
</template>
